// Button — two variants and two states. The disabled state never
// animates (calm-state rule, README §6.2). All transitions honour
// `prefers-reduced-motion` via the global CSS guard (styles.css).
//
// See README §6.1 (Activate button), §7.1 (terracotta primary action),
// §7.2 (motion).

import type { ReactNode } from 'react';
import { theme } from '../theme';

export type ButtonVariant = 'primary' | 'secondary';
export type ButtonState = 'enabled' | 'disabled';

export interface ButtonProps {
  variant?: ButtonVariant;
  state?: ButtonState;
  onClick?: () => void;
  children: ReactNode;
  /** Optional explicit aria-label when the children are iconic. */
  ariaLabel?: string;
  type?: 'button' | 'submit';
}

export function Button({
  variant = 'primary',
  state = 'enabled',
  onClick,
  children,
  ariaLabel,
  type = 'button',
}: ButtonProps) {
  const disabled = state === 'disabled';
  const isPrimary = variant === 'primary';
  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={disabled ? 'cc-btn cc-btn-disabled' : 'cc-btn'}
      style={{
        minHeight: '44px',
        padding: '0.6rem 1.2rem',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: isPrimary ? (disabled ? theme.bg.muted : theme.accent.action) : 'transparent',
        color: isPrimary
          ? disabled
            ? theme.text.muted
            : theme.surface.onAccent
          : theme.text.primary,
        border: isPrimary
          ? '1px solid transparent'
          : `1px solid ${disabled ? theme.text.muted : theme.text.primary}`,
        opacity: disabled ? 0.7 : 1,
        // No transition on the disabled path — the .cc-btn-disabled
        // class strips animations entirely (see styles.css).
        transition: disabled
          ? 'none'
          : `background ${theme.motion.duration.fast} ${theme.motion.easing}, color ${theme.motion.duration.fast} ${theme.motion.easing}`,
      }}
    >
      {children}
    </button>
  );
}

export default Button;
